// pages/Admin/Users/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    Typography,
    Button,
    MenuItem,
    Skeleton,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import AddIcon from "@mui/icons-material/Add";
import UsersTable from "./UsersTable";
import UserDialog from "./UserDialog";
import { CssTextField } from "qubCommon/Inputs/CssTextField";
import { getUsers } from "apis/controllers/users";

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [genders, setGenders] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [canCreate, setCanCreate] = useState(false);
    const [loading, setLoading] = useState(true);

    const [openDialog, setOpenDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // FILTER & SORT STATES
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterGender, setFilterGender] = useState("");
    const [filterRole, setFilterRole] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await getUsers();
            setUsers(res?.data?.users || []);
            setRoles(res?.data?.lookups?.roles || []);
            setGenders(res?.data?.lookups?.gender || []);
            setStatuses(res?.data?.lookups?.userStatus || []);
            setCanCreate(res?.data?.canCreate || false);
        } catch (error) {
            console.error("Failed to load users:", error);
        } finally {
            setLoading(false);
        }
    };

    // FILTER + SEARCH + SORT LOGIC
    const filteredUsers = useMemo(() => {
        let data = [...users];

        if (search.trim() !== "") {
            const term = search.toLowerCase();
            data = data.filter(
                (u) =>
                    u.lastName.toLowerCase().includes(term) ||
                    u.firstName.toLowerCase().includes(term) ||
                    (u.lastName + " " + u.firstName).toLowerCase().includes(term) ||
                    u.email.toLowerCase().includes(term)
            );
        }

        // Filter by selected status ID
        if (filterStatus) {
            data = data.filter((u) => u.status.id === filterStatus)
        }

        // Filter by selected gender ID
        if (filterGender) {
            console.log(filterGender, genders)
            data = data.filter((u) => u.gender.id === filterGender);
        }

        // Filter by selected role ID
        if (filterRole) {
            data = data.filter((u) => u.roles?.some((r) => r.id === filterRole));
        }

        // Sort
        if (sortBy) {
            data.sort((a, b) => {
                let A, B;
                if (sortBy === "fullName") {
                    A = `${a.lastName || ""} ${a.firstName || ""}`.toLowerCase();
                    B = `${b.lastName || ""} ${b.firstName || ""}`.toLowerCase();
                } else {
                    A = a[sortBy] ?? "";
                    B = b[sortBy] ?? "";
                    if (typeof A === "string") A = A.toLowerCase();
                    if (typeof B === "string") B = B.toLowerCase();
                }
                const cmp = A > B ? 1 : A < B ? -1 : 0;
                return sortDirection === "asc" ? cmp : -cmp;
            });
        }

        return data;
    }, [users, search, filterStatus, filterGender, filterRole, sortBy, sortDirection, genders, statuses]);

    const handleSortChange = (field) => {
        if (sortBy === field) setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        else {
            setSortBy(field);
            setSortDirection("asc");
        }
    };

    // CRUD HANDLERS
    const handleNewUser = () => {
        setEditingUser(null);
        setOpenDialog(true);
    };

    const handleSelectUser = (user) => {
        setEditingUser(user);
        setOpenDialog(true);
    };

    const handleSaveUser = (data) => {
        const roleObjects = roles.filter((r) => data.roles?.includes(r.id));
        setUsers((prev) => {
            const exists = prev.find((u) => u.id === data.id);
            if (!exists) return [...prev, { ...data, id: Date.now(), roles: roleObjects }];
            return prev.map((u) => (u.id === data.id ? { ...u, ...data, roles: roleObjects } : u));
        });
    };

    const clearFilters = () => {
        setSearch("");
        setFilterStatus("");
        setFilterGender("");
        setFilterRole("");
        setSortBy("");
        setSortDirection("asc");
    };


    return (
        <Box sx={{ p: 3, maxWidth: "90%", mx: "auto", minHeight: "70vh" }}>
            {/* HEADER */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Stack direction="row" gap={1} alignItems="center">
                    <PeopleIcon color="primary" />
                    <Typography variant="h3" fontWeight={700}>
                        Χρήστες
                    </Typography>
                </Stack>
                {canCreate && <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleNewUser}
                    sx={{ borderRadius: 3 }}
                >
                    Νέος Χρήστης
                </Button>}
            </Stack>

            {/* FILTER BAR */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                mb={2}
                alignItems={{ xs: "stretch", sm: "flex-end" }}
                sx={{ p: 1 }}
            >
                {loading ? (
                    <>
                        <Skeleton variant="rectangular" width={260} height={40} />
                        <Skeleton variant="rectangular" width={180} height={40} />
                        <Skeleton variant="rectangular" width={150} height={40} />
                        <Skeleton variant="rectangular" width={220} height={40} />
                        <Skeleton variant="rectangular" width={100} height={38} />
                    </>
                ) : (
                    <>
                        {/* SEARCH */}
                        <Stack spacing={0.5} sx={{ width: { xs: "100%", sm: 260 } }}>
                            <Typography sx={{ fontWeight: 600 }}>Αναζήτηση</Typography>
                            <CssTextField
                                placeholder="Όνομα ή Email"
                                name='filterEmail'
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                variant="outlined"
                                autoComplete="nope" // <-- add this
                            />
                        </Stack>

                        {/* STATUS */}
                        <Stack spacing={0.5} sx={{ width: { xs: "100%", sm: 180 } }}>
                            <Typography sx={{ fontWeight: 600 }}>Κατάσταση</Typography>
                            <CssTextField
                                select
                                autoComplete="off" // <-- add this
                                variant="outlined"
                                size="small"
                                SelectProps={{
                                    value: filterStatus,
                                    onChange: (e) => setFilterStatus(e.target.value),
                                    displayEmpty: true,
                                    renderValue: (selected) =>
                                        selected === "" ? (
                                            <em style={{ color: "#9e9e9e" }}>Όλα</em>
                                        ) : (
                                            statuses.find((s) => s.id === Number(selected))?.name
                                        ),
                                }}
                            >
                                <MenuItem value="">
                                    <em>Όλα</em>
                                </MenuItem>
                                {statuses.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </CssTextField>
                        </Stack>

                        {/* GENDER */}
                        <Stack spacing={0.5} sx={{ width: { xs: "100%", sm: 150 } }}>
                            <Typography sx={{ fontWeight: 600 }}>Φύλο</Typography>
                            <CssTextField
                                autoComplete="off" // <-- add this
                                select
                                variant="outlined"
                                size="small"
                                value={filterGender}
                                onChange={(e) => setFilterGender(e.target.value)}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) =>
                                        selected === "" ? (
                                            <em style={{ color: "#9e9e9e" }}>Όλα</em>
                                        ) : (
                                            genders.find((g) => g.id === Number(selected))?.name
                                        ),
                                }}
                            >
                                <MenuItem value="">
                                    <em>Όλα</em>
                                </MenuItem>
                                {genders.map((g) => (
                                    <MenuItem key={g.id} value={g.id}>
                                        {g.name}
                                    </MenuItem>
                                ))}
                            </CssTextField>
                        </Stack>

                        {/* ROLE */}
                        <Stack spacing={0.5} sx={{ width: { xs: "100%", sm: 220 } }}>
                            <Typography sx={{ fontWeight: 600 }}>Ρόλος</Typography>
                            <CssTextField
                                autoComplete="off" // <-- add this
                                select
                                variant="outlined"
                                size="small"
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) =>
                                        selected === "" ? (
                                            <em style={{ color: "#9e9e9e" }}>Όλοι οι ρόλοι</em>
                                        ) : (
                                            roles.find((r) => r.id === selected)?.name
                                        ),
                                }}
                            >
                                <MenuItem value="">
                                    <em>Όλοι οι ρόλοι</em>
                                </MenuItem>
                                {roles.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>
                                        {r.name}
                                    </MenuItem>
                                ))}
                            </CssTextField>
                        </Stack>

                        {/* CLEAR BUTTON */}
                        <Box>
                            <Button
                                color="error"
                                onClick={clearFilters}
                                variant="contained"
                                sx={{ height: 38, borderRadius: 2 }}
                            >
                                Καθαρισμός
                            </Button>
                        </Box>
                    </>
                )}
            </Stack>

            {/* USERS TABLE */}
            {loading ? (
                <Skeleton variant="rectangular" width="100%" height={400} />
            ) : (
                <UsersTable
                    rows={filteredUsers}
                    onSelect={handleSelectUser}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSortChange={handleSortChange}
                />
            )}

            <UserDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onSubmit={handleSaveUser}
                defaultValues={editingUser || {}}
                roleOptions={roles}
                genderOptions={genders}
                loadUsers={loadUsers}
            />
        </Box>
    );
};

export default UsersPage;
